import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ApiService } from './api.service';

/**
 * "Add to STAC" on My Collections (plans/stac-conversion-eetaskapi.md §8.2): one shared state per
 * task, read from eetaskapi's GET /tasks/stac-conversion/:taskid, for the task's button
 * (app-stac-conversion) and its scenes' status chips (app-stac-scene-status).
 *
 * The task's button does the reading: once when the task is shown, after a press, and every few
 * seconds while its request is queued or running. The scene chips only listen, so a task's scenes
 * cost no requests of their own.
 *
 * The server decides whether the feature is there at all. While on-demand is switched off its
 * routes aren't mounted and the GET answers 404, as does a task not delivered to platform storage
 * (decision 40); a plan without STAC, or a task the user can't see, answers 403. Either way the
 * state is `null` and nothing is shown. The page never POSTs
 * before a GET has answered: with on-demand off, a POST to that path would reach tasks.route.js's
 * `/:action/:taskid` catch-all, the ordering route.
 */

export type StacRequestStatus = 'queued' | 'running' | 'done' | 'partial' | 'failed';
export type StacSceneStatusCode = 'converting' | 'interrupted' | 'uploaded' | 'success' | 'skipped' | 'error';

export interface StacConversionRequest {
  requestId: string;
  status: StacRequestStatus;
  errorCode?: string;
  requestedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  converted?: number;
  alreadyDone?: number;
  failed?: number;
  skipped?: number;
  pending?: number;
  archivedSensors: string[];
}

export interface StacSceneState {
  sensorUuid: string;
  sceneId: string;
  status: StacSceneStatusCode;
  errorCode?: string;
}

/**
 * What a request does with a sensor (eetaskapi stac/ondemand/operators.js sensorCoverage): its
 * operator is switched on; not yet (decision 31: its scenes are left alone, with no entry); or
 * STAC doesn't cover it at all.
 */
export type StacSensorCoverage = 'on' | 'off' | 'none';

export interface StacTaskState {
  /** The latest request as the route shows it (an expired one as failed), or null if there's none. */
  request: StacConversionRequest | null;
  /** The task's scenes that STAC has an entry for, by StacConversionService.sceneKey(). */
  scenes: Map<string, StacSceneState>;
  /** What a request does with each of the task's sensors, by sensor uuid. */
  sensors: Map<string, StacSensorCoverage>;
  stacProcessingDone: boolean;
}

/** undefined: not known yet. null: no "Add to STAC" here for this user, so nothing is shown. */
export type StacTaskView = StacTaskState | null | undefined;

/** Why a press didn't queue a request, for the button's tooltip; `type` picks its icon. */
export interface StacPressNotice {
  message: string;
  type: 'warning' | 'error';
}

export interface StacPressOutcome {
  ok: boolean;
  /**
   * What the button adds to its tooltip; null where the task's state, read again after the press,
   * says it already: a request queued, one already active, or one that couldn't be queued.
   */
  notice: StacPressNotice | null;
}

/** Shown on a scene, and why (plans/stac-conversion-eetaskapi.md §4.4, §8.2). */
export interface StacSceneLabel {
  text: string;
  tone: 'in' | 'working' | 'attention' | 'off' | 'failed';
  /** Hover text; empty for a chip that says it all (In Data Catalog). */
  tooltip: string;
}

/**
 * A scene's hint. It names no state: the button says "Sync Data Catalog" whatever the task's state
 * (StacConversionComponent).
 */
const RETRY_HINT = 'Try again with Sync Data Catalog, or contact support.';
/** The button's own hint, in its tooltip. */
const TRY_AGAIN = 'Try again, or contact support.';

/** §4.4: what each scene error code shows. */
const ERROR_TEXT: Readonly<Record<string, string>> = Object.freeze({
  busy: 'Not synced yet',
  no_image: 'No image found',
  source_missing: 'Delivery file not found',
  not_publishable: 'Not available in Data Catalog',
  task_ambiguous: 'Contact support',
  extract_failed: 'Sync failed',
  upload_failed: 'Sync failed',
  catalog_failed: 'Sync failed',
});

const COVERAGES: readonly string[] = ['on', 'off', 'none'];

@Injectable({ providedIn: 'root' })
export class StacConversionService {
  /** Platform JWT the states below belong to: a different account in this tab starts afresh. */
  private owner: string | null = null;
  private readonly views = new Map<string, BehaviorSubject<StacTaskView>>();
  private readonly reading = new Map<string, Promise<StacTaskView>>();

  constructor(private api: ApiService) {}

  /**
   * A scene's key, the way eetaskapi keys its entries (stac/records.js `sceneIdOf`): the sensor's
   * uuid and the scene's `scene_id`, else its `id`, trimmed. A 21AT L1 array id is read joined, as
   * the delivery's own keys join it. On the collections page's rebuilt objects the sensor's uuid
   * is `sensor.id` (see StacTagsService.candidateItemId). Null when either part is missing.
   */
  static sceneKey(sensorUuid: unknown, scene: any): string | null {
    const joined = (value: unknown) => (Array.isArray(value) ? String(value) : value);
    const id = joined(scene?.scene_id) || joined(scene?.id);
    if (typeof sensorUuid !== 'string' || sensorUuid === '' || typeof id !== 'string' || id.trim() === '') return null;
    return sensorUuid + '\u0000' + id.trim();
  }

  /**
   * What a scene shows, from its entry. Null when it has none, or its source was archived
   * (`source_archived`): the scene hasn't been added to STAC, its sensor is archived (the Data
   * Catalog banner says to restore it, StacConversionComponent), or its sensor is one a request
   * leaves alone — not switched on yet, or not covered at all, such as one ordered to the
   * customer's own storage — where no reason is shown. An amber or red label's tooltip says it all,
   * starting with its text (an interrupted scene's in its own words): the scene shows it alone, as
   * an icon's hover text (StacSceneStatusComponent).
   */
  static sceneLabel(entry: StacSceneState | undefined): StacSceneLabel | null {
    if (!entry || (entry.status === 'error' && entry.errorCode === 'source_archived')) return null;
    switch (entry.status) {
      case 'success':
        return { text: 'In Data Catalog', tone: 'in', tooltip: '' };
      // In progress, one chip: a first sync, or a re-sync of a scene already in the catalog.
      case 'converting':
      case 'uploaded':
        return { text: 'Syncing', tone: 'working', tooltip: 'It is being synced to the Data Catalog.' };
      case 'interrupted':
        return { text: 'Sync interrupted', tone: 'attention', tooltip: 'The scene addition to the Data Catalog stopped before completion. Please try adding by clicking on Sync Data Catalog.' };
      case 'skipped':
        return { text: 'Not available in Data Catalog', tone: 'off', tooltip: "This scene can't be added to the Data Catalog." };
      default: {
        const text = ERROR_TEXT[entry.errorCode || ''] || 'Sync failed';
        const tooltip = entry.errorCode === 'task_ambiguous' ? text + ": this task can't be added to the Data Catalog." : text + '. ' + RETRY_HINT;
        return { text, tone: entry.errorCode === 'busy' ? 'attention' : 'failed', tooltip };
      }
    }
  }

  /**
   * The ids of the task's sensors with at least one delivered scene, from the task as the
   * collections page holds it: a scene with an id whose status isn't failed, which is the page's
   * own rule for the scene's download button. A planned capture has a scene_id but no id.
   */
  static deliveredSensors(sensors: any): Set<string> {
    const out = new Set<string>();
    for (const sensor of Array.isArray(sensors) ? sensors : []) {
      if (!sensor || typeof sensor.id !== 'string' || !sensor.id) continue;
      const scenes = Array.isArray(sensor.scenes) ? sensor.scenes : [];
      if (scenes.some((scene: any) => !!scene && !!scene.id && scene.status !== 'failed')) out.add(sensor.id);
    }
    return out;
  }

  /**
   * Whether a press would do anything: Add to STAC is switched on for at least one of the task's
   * sensors that has a delivered scene. A task still waiting for its first delivery shows no
   * button, as the route would refuse it with nothing_delivered.
   */
  static anyDeliveredOn(sensors: Map<string, StacSensorCoverage>, delivered: ReadonlySet<string>): boolean {
    for (const [sensorUuid, coverage] of sensors) if (coverage === 'on' && delivered.has(sensorUuid)) return true;
    return false;
  }

  /** Is a request queued or running, so that the button waits? */
  static isActive(request: StacConversionRequest | null): boolean {
    return !!request && (request.status === 'queued' || request.status === 'running');
  }

  /** The task's state as it changes. Reading it doesn't ask the server; refresh() does. */
  view$(taskId: string): Observable<StacTaskView> {
    this.assertOwner();
    return this.subject(taskId).asObservable();
  }

  /**
   * Asks the server for the task's state, and passes it on to every listener. Concurrent callers
   * share one request. A 401, 403 or 404 means no "Add to STAC" here: `null`. Any other failure
   * rejects, and leaves what was shown as it was.
   */
  refresh(taskId: string): Promise<StacTaskView> {
    this.assertOwner();
    const pending = this.reading.get(taskId);
    if (pending) return pending;
    const read = firstValueFrom(this.api.getStacConversion(taskId))
      .then((body) => toState(body))
      .catch((err: unknown) => {
        if (err instanceof HttpErrorResponse && [401, 403, 404].includes(err.status)) return null;
        throw err;
      })
      .then((view) => {
        this.subject(taskId).next(view);
        return view;
      })
      .finally(() => this.reading.delete(taskId));
    this.reading.set(taskId, read);
    return read;
  }

  /** Presses the button. Never rejects: the outcome says what to tell the user, if anything. */
  async press(taskId: string): Promise<StacPressOutcome> {
    this.assertOwner();
    let outcome: StacPressOutcome;
    try {
      await firstValueFrom(this.api.requestStacConversion(taskId));
      outcome = { ok: true, notice: null };
    } catch (err: unknown) {
      outcome = { ok: false, notice: refusal(err) };
    }
    await this.refresh(taskId).catch(() => undefined);
    return outcome;
  }

  private subject(taskId: string): BehaviorSubject<StacTaskView> {
    let subject = this.views.get(taskId);
    if (!subject) {
      subject = new BehaviorSubject<StacTaskView>(undefined);
      this.views.set(taskId, subject);
    }
    return subject;
  }

  private assertOwner(): void {
    const token = sessionStorage.getItem('jwttoken');
    if (token !== this.owner) {
      this.owner = token;
      for (const subject of this.views.values()) subject.next(undefined);
      this.views.clear();
      this.reading.clear();
    }
  }
}

/** The route's fixed answer (§6.1), as the page keeps it. */
function toState(body: any): StacTaskState {
  const scenes = new Map<string, StacSceneState>();
  for (const scene of (body && Array.isArray(body.scenes) ? body.scenes : []) as StacSceneState[]) {
    if (scene && typeof scene.sensorUuid === 'string' && typeof scene.sceneId === 'string') {
      scenes.set(scene.sensorUuid + '\u0000' + scene.sceneId, scene);
    }
  }
  const sensors = new Map<string, StacSensorCoverage>();
  for (const sensor of (body && Array.isArray(body.sensors) ? body.sensors : []) as Array<{ sensorUuid: string; coverage: StacSensorCoverage }>) {
    if (sensor && typeof sensor.sensorUuid === 'string' && COVERAGES.indexOf(sensor.coverage) !== -1) sensors.set(sensor.sensorUuid, sensor.coverage);
  }
  const request = body && body.request ? Object.assign({ archivedSensors: [] }, body.request) : null;
  return { request, scenes, sensors, stacProcessingDone: !!(body && body.stacProcessingDone) };
}

/**
 * Why a press didn't queue a request, in the route's own terms (routes/stac.route.js, §4.4); null
 * where the state read after the press shows it: the active request, or the one that couldn't be
 * queued, which the route records as failed.
 */
function refusal(err: unknown): StacPressNotice | null {
  if (!(err instanceof HttpErrorResponse) || !err.status) return { message: "The server couldn't be reached. Try again.", type: 'error' };
  const code = err.error && typeof err.error === 'object' ? err.error.error : undefined;
  switch (code) {
    case 'request_active':
    case 'enqueue_failed':
      return null;
    case 'all_archived':
      return { message: 'Restore first: every sensor of this task with delivered scenes is archived.', type: 'warning' };
    case 'nothing_delivered':
      return { message: 'Nothing has been delivered for this task yet.', type: 'warning' };
    case 'task_ambiguous':
      return { message: "This task can't be added to the Data Catalog. Please contact support.", type: 'error' };
    case 'not_enabled':
    case 'plan_not_eligible':
    case 'forbidden':
    case 'not_found':
      return { message: "The Data Catalog isn't available for this task.", type: 'warning' };
    default:
      return { message: 'The request failed. ' + TRY_AGAIN, type: 'error' };
  }
}
